/**
 * profileSections.js — reading a profile section off the wire, once.
 *
 * ★★★ WHY. `profiles.*` are JSONB columns, and a writer that serialises before
 *     writing puts a JSON *string* in the column instead of an object. The API did
 *     exactly that for `medications` (fixed in v243.138.0), and the client read it
 *     straight through — so `medications.completed` was read off a string, came
 *     back undefined, and a section the patient had completed rendered as
 *     incomplete with an empty list.
 *
 * ★★★ AND THE LENGTH GUARD HID IT. The old code gated each assignment on
 *     `Object.keys(section).length > 0`. On a string that returns CHARACTER
 *     INDICES, so the guard passed with flying colours on exactly the input it
 *     should have caught. A truthiness test that is true for the broken shape is
 *     not a guard.
 *
 * ★★ The API's own readers have carried this parse for a long time
 *    (`parse_json_field`, `parse_nested_json`). This is the client half, and it
 *    also repairs rows already corrupted, with no data migration.
 */

/** API row key → profile-store key. The store is camelCase; the row is snake_case. */
export const PROFILE_SECTION_KEYS = Object.freeze({
  identification: 'identification',
  insurance: 'insurance',
  allergies: 'allergies',
  medications: 'medications',
  medical_history: 'medicalHistory',
  family_history: 'familyHistory',
  social_lifestyle: 'socialLifestyle',
})

/**
 * A section as a plain object, or null if it is not one.
 *
 * Accepts an object, or a JSON string holding one. Returns null for anything
 * else — an array, a bare number, unparseable text — so a caller can never
 * assign a non-section into the store and read `undefined` off it later.
 */
export function parseSection(raw) {
  if (raw == null) return null
  let v = raw
  if (typeof v === 'string') {
    try { v = JSON.parse(v) } catch { return null }
  }
  if (!v || typeof v !== 'object' || Array.isArray(v)) return null
  return v
}

/** True when a parsed section carries anything at all. Never true for a string. */
export function hasSectionContent(raw) {
  const v = parseSection(raw)
  return !!v && Object.keys(v).length > 0
}
